import {m as motion, useScroll, useMotionValueEvent} from 'motion/react'
import React from 'react'
import {InView} from 'react-intersection-observer'
import styled from 'styled-components'

import GatsbyImage from '@/components/image'
import {ImageCard} from '@/components/image-card'
import PrismicLink from '@/components/prismic-link'
import {Text} from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import {Section} from '@/styles/grid'
import {tokens} from '@/theme'
import {getVideoSources} from '@/utils/get-video-sources'
import mq from '@/utils/media-query'

import type {ImageCardProps} from '@/components/image-card'
import type {StickyCardsFragment, SlicePageStickyCardsFragment} from '@/graphql-types'

type Props = {
    slice: StickyCardsFragment | SlicePageStickyCardsFragment
}

export const StickyCards = React.forwardRef<HTMLDivElement, Props>(({slice}, ref) => {
    const sentinelRefs = React.useRef<(HTMLDivElement | null)[]>([])
    const cardRefs = React.useRef<(HTMLDivElement | null)[]>([])
    const [distances, setDistances] = React.useState<number[]>([])
    const [isInView, setIsInView] = React.useState<boolean>(false)

    const isDesktop = useMediaQuery.min('md')
    const {scrollY} = useScroll()

    const {
        primary: {headline},
        items,
    } = slice

    const calculateDistances = () => {
        let newDistances: number[] = []

        if (sentinelRefs.current.length > 0 && cardRefs.current.length > 0) {
            sentinelRefs.current.forEach((sentinel, index) => {
                if (cardRefs.current[index] && sentinel) {
                    const sentinelRect = sentinel?.getBoundingClientRect()
                    const cardRect = cardRefs.current[index]?.getBoundingClientRect()
                    const yDistance = cardRect ? cardRect.top - sentinelRect.top : 0

                    newDistances.push(Math.floor(yDistance))
                }
            })

            setDistances(newDistances)
        }
    }

    useMotionValueEvent(scrollY, 'change', () => {
        if (isDesktop && isInView) {
            calculateDistances()
        }
    })

    return (
        <InView onChange={setIsInView}>
            <Wrapper ref={ref}>
                <StyledText $variant="heading-large">{headline}</StyledText>

                {items.map(
                    (
                        {
                            headline,
                            description,
                            videoPosterImage,
                            backgroundColor,
                            videoMp4,
                            image,
                            ctaLabel,
                            ctaLink,
                        },
                        index,
                    ) => (
                        <React.Fragment key={headline}>
                            <Sentinel
                                ref={(el) => (sentinelRefs.current[index] = el)}
                                aria-hidden
                            />
                            <StyledImageCard
                                key={index}
                                ref={(el: HTMLDivElement | null) =>
                                    (cardRefs.current[index] = el as HTMLDivElement)
                                }
                                style={{
                                    transform: `scale(${
                                        isDesktop ? Math.max(1 - distances[index] / 2000, 0.4) : 1
                                    })`,
                                }}
                                headline={headline}
                                description={description}
                                videoPoster={videoPosterImage}
                                backgroundColor={
                                    backgroundColor as ImageCardProps['backgroundColor']
                                }
                                Image={(props) => <GatsbyImage {...props} image={image} />}
                                video={getVideoSources({
                                    mp4: videoMp4,
                                })}
                                CTA={(props) => (
                                    <PrismicLink {...props} {...ctaLink}>
                                        {ctaLabel}
                                    </PrismicLink>
                                )}
                            />
                        </React.Fragment>
                    ),
                )}
            </Wrapper>
        </InView>
    )
})

const LAPTOP_BP = 'md'

const Wrapper = styled(Section)`
    display: flex;
    flex-direction: column;
`

const Sentinel = styled.div`
    &:not(:first-of-type) {
        margin-top: ${tokens.spacing20};

        ${mq.min(LAPTOP_BP)} {
            margin-top: ${tokens.spacing40};
        }
    }
`

const StyledText = styled(Text)`
    margin-bottom: ${tokens.spacing40};

    ${mq.min(LAPTOP_BP)} {
        position: sticky;
        top: 120px;
        margin-bottom: 80px;
        text-align: center;
    }
`

const StyledImageCard = styled(motion.create(ImageCard))`
    will-change: filter, transform;

    ${mq.min(LAPTOP_BP)} {
        position: sticky;
        top: 260px;
    }
`
